import type { Dispatch, SetStateAction } from 'react';

import { BackrsFormValues } from '@backrs_ui/common-ui';

import { AutocompleteResult } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { UseFormReturn } from 'react-hook-form';

import { StepKey } from './stepNavigation';
import { OnboardingQuestion } from '../onboarding.types';

type TriggerOnboardingValidationProps = Readonly<{
  currentQuestion: OnboardingQuestion | undefined;
  formMethods: UseFormReturn<BackrsFormValues>;
  currentStep: StepKey;
}>;

// Trigger validation for fields that have existing values when navigating back
export const triggerOnboardingValidation = ({
  currentQuestion,
  formMethods,
  currentStep,
}: TriggerOnboardingValidationProps) => {
  if (!currentQuestion) return;

  const formValues = formMethods.getValues();
  const fieldsToValidate: string[] = [];

  // For RADIO/CHECKBOX types, the field name is the step key
  if (currentQuestion.question.type === 'RADIO' || currentQuestion.question.type === 'CHECKBOX') {
    if (formValues[currentStep as keyof BackrsFormValues]) {
      fieldsToValidate.push(currentStep);
    }
  }

  // For FORM types, trigger validation for each field that has a value
  if (currentQuestion.question.type === 'FORM') {
    currentQuestion.question.fields.forEach((field) => {
      const fieldName = field.name;

      if (fieldName && formValues[fieldName as keyof BackrsFormValues]) {
        fieldsToValidate.push(fieldName);
      }
    });
  }

  // Trigger validation for all collected fields
  fieldsToValidate.forEach((fieldName) => {
    formMethods.trigger(fieldName as keyof BackrsFormValues);
  });
};

type RestoreAutocompleteOptionsProps = Readonly<{
  currentQuestion: OnboardingQuestion | undefined;
  formMethods: UseFormReturn<BackrsFormValues>;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: Dispatch<SetStateAction<AutocompleteResult[]>>;
}>;

// Restore autocomplete options for fields that have values when navigating back
export const restoreAutocompleteOptions = ({
  currentQuestion,
  formMethods,
  autoCompleteOptions,
  setAutoCompleteOptions,
}: RestoreAutocompleteOptionsProps) => {
  if (!currentQuestion || currentQuestion.question.type !== 'FORM') return;

  const formValues = formMethods.getValues();

  currentQuestion.question.fields.forEach((field) => {
    const endpoint = field.autoCompleteEndpoint;

    if (endpoint && formValues[field.name as keyof BackrsFormValues]) {
      const fieldValue = formValues[field.name as keyof BackrsFormValues];
      const existingOption = autoCompleteOptions.find((option) => option.endpoint === endpoint);

      // If we don't have this endpoint in state yet, or it has no values but the form has a value
      if (!existingOption || (existingOption.values.length === 0 && fieldValue)) {
        const valueArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

        setAutoCompleteOptions((prev) => {
          const updatedOptions = [...prev];
          const index = updatedOptions.findIndex((opt) => opt.endpoint === endpoint);

          const newValues = valueArray.map((val) => ({
            label: String(val),
            value: String(val),
          }));

          if (index >= 0) {
            // Update existing
            updatedOptions[index] = {
              ...updatedOptions[index],
              values: newValues,
            };
          } else {
            // Add new
            updatedOptions.push({
              endpoint,
              searchTerm: '',
              values: newValues,
            });
          }

          return updatedOptions;
        });
      }
    }
  });
};

type UpdateLocalAutocompleteOptionsProps = Readonly<{
  autocompleteData: AutocompleteResult[] | undefined;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: Dispatch<SetStateAction<AutocompleteResult[]>>;
}>;
export const updateLocalAutocompleteOptions = ({
  autocompleteData,
  setAutoCompleteOptions,
  autoCompleteOptions,
}: UpdateLocalAutocompleteOptionsProps) => {
  if (autocompleteData && Array.isArray(autocompleteData) && autocompleteData.length) {
    const newOptions = autocompleteData.map((data) => {
      const existingOption = autoCompleteOptions.find(
        (option) => option.endpoint === data.endpoint,
      );

      return {
        ...data,
        searchTerm: existingOption ? existingOption.searchTerm : '',
      };
    });

    if (JSON.stringify(newOptions) !== JSON.stringify(autoCompleteOptions)) {
      setAutoCompleteOptions(newOptions);
    }
  }
};

/**
 * Parses a full name string into first_name and last_name components.
 * Expects a validated full name with at least first and last name separated by space.
 *
 * @example
 * parseName("John Smith") // { first_name: "John", last_name: "Smith" }
 * parseName("Mary Ann Johnson") // { first_name: "Mary", last_name: "Ann Johnson" }
 */
export const parseName = (fullName: string): { first_name: string; last_name: string } => {
  const trimmed = fullName.trim();
  const firstSpaceIndex = trimmed.indexOf(' ');

  return {
    first_name: trimmed.substring(0, firstSpaceIndex),
    last_name: trimmed.substring(firstSpaceIndex + 1).trim(),
  };
};
